/**
 * InsCipher trigger row — OR-1440.
 *
 * Sits inside Taxes & Fees as the header of a grouped block: this row carries
 * the "InsCipher" label, a provenance mark and the Calculate button; the
 * Surplus Lines Tax / Fee rows render underneath it, shaded as one group.
 *
 * Why it's a button and not automatic: InsCipher's quota is roughly 1,000
 * requests per YEAR, so the calculation is user-initiated by design. The
 * backend also gates it on the submission being in "Indications Sent" and
 * dedupes identical inputs, so a second press with nothing changed costs
 * nothing.
 *
 * Copy rule (Gustavo, 4-ago): the approved design's wording wins wherever it
 * defines a state. For a state the design doesn't cover — only the "already up
 * to date" outcome — the backend's own message is shown as-is rather than
 * invented here.
 */
import { RefreshCw02 } from "@untitledui/icons";
import { Button, Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { CheckCircle, InfoCircle, XCircle } from "@untitledui/icons";
import { formatDateTime } from "../../../../utils/format-datetime";

/**
 * `current` is the "nothing to recalculate" outcome — the backend refuses a
 * repeat calculation with a 409 when none of the fingerprinted inputs moved
 * (backend PR #327). It is not a failure, so it must not render the error mark.
 */
export type TaxCalcPhase = "idle" | "loading" | "done" | "current" | "error";

/**
 * Button tooltips. The line breaks are deliberate: atlas' tooltip is capped at
 * `max-w-xs` (320px) and hugs its content, so left to itself it wraps wider
 * than the Figma's 219 / 170. `title` renders `whitespace-pre-line`, so
 * breaking here fixes both the wrap and the width to the design.
 */
/** Shown when the button can't be pressed yet — the backend rejects anything
 *  that isn't "Indications Sent" with a 409. */
const TOOLTIP_DISABLED = "Calculate Surplus Lines Tax & Fee\nonce status is Indications Sent";
const TOOLTIP_ENABLED = "Calculates Surplus Lines\nTax & Fee via InsCipher";
/** Failure copy, verbatim from the approved design — used for every failure,
 *  including precondition ones, so the wording stays the design's. */
export const TAX_CALC_ERROR = "Couldn't calculate Surplus Lines Tax & Fee. Retry.";

interface InsCipherRowProps {
    /** False before the submission reaches "Indications Sent". */
    canCalculate: boolean;
    phase: TaxCalcPhase;
    /**
     * Tooltip for the `current` mark. The design has no "already up to date"
     * state, so per the copy rule above this passes the backend's own message
     * through unchanged — it already names the fields that would trigger a
     * recalculation ("premium, effective date and home state…").
     */
    currentMessage?: string;
    /**
     * Whether the surplus-lines values currently shown came from InsCipher.
     * Drives the ✓. Derived by the page from the persisted amounts, so it
     * survives a reload — and goes away as soon as a rate is edited by hand,
     * per the design.
     */
    isInsCipherAuthored: boolean;
    /**
     * ISO datetime of the last InsCipher run (`quote.tax_calculated_at`), or
     * null when the column isn't there yet / never ran. Non-null renders the
     * design's timestamp line under the ✓ tooltip title; null renders
     * title-only, so this whole path stays dormant until backend PR #327 ships
     * the field and needs no change then.
     */
    calculatedAt?: string | null;
    onCalculate: () => void;
    /** Custom attribute for E2E testing — applied to the row; the Calculate
     *  button gets `${dataAutotest}-calculate-button`. */
    "data-autotest"?: string;
}

export function InsCipherRow({
    canCalculate,
    phase,
    currentMessage,
    isInsCipherAuthored,
    calculatedAt,
    onCalculate,
    "data-autotest": dataAutotest,
}: InsCipherRowProps) {
    const isLoading = phase === "loading";
    // The ✓ tracks provenance, not "the last press succeeded": a reload has no
    // memory of the press but the persisted amounts still came from InsCipher.
    const showCheck = isInsCipherAuthored && phase !== "error";

    return (
        <div data-autotest={dataAutotest} className="flex min-h-15 items-center justify-between gap-3 py-3">
            <span className="flex items-center gap-1.5">
                {/* Black, not the muted label color the other rows use — the
                  * design sets it apart as the group's heading. */}
                <span className="text-primary text-sm font-semibold">InsCipher</span>

                {showCheck && (
                    // Title broken where the design breaks it, which pins the
                    // tooltip to its 164px width (see the button tooltips above).
                    // The timestamp is the design's second block — atlas renders
                    // `description` in the lighter supporting color, which is
                    // what the Figma shows.
                    //
                    // `description` is undefined until backend PR #327 ships
                    // `tax_calculated_at`, so today the tooltip is title-only at
                    // 52 tall instead of the design's 82. Nothing to change here
                    // when it merges — the field just starts arriving.
                    //
                    // Caveat to raise with design once it renders: the field
                    // means "when InsCipher last ran", not "when these numbers
                    // were produced". Change the premium without recalculating
                    // and this line goes stale while the copy still reads as
                    // though it describes the values on screen.
                    <Tooltip
                        title={"Surplus Lines Tax & Fee\ncalculated via InsCipher"}
                        description={formatDateTime(calculatedAt, "at") || undefined}
                        placement="top"
                    >
                        <TooltipTrigger className="cursor-help">
                            <CheckCircle aria-label="Calculated via InsCipher" className="text-fg-success-primary size-4" />
                        </TooltipTrigger>
                    </Tooltip>
                )}

                {/* "Already current" — a press that had nothing to recalculate.
                  * Neutral on purpose: the numbers on screen are fine, so the
                  * red ⊗ would be a lie. Shown next to the ✓, not instead of
                  * it, so provenance still reads. */}
                {phase === "current" && currentMessage && (
                    <Tooltip title={currentMessage} placement="top">
                        <TooltipTrigger className="cursor-help">
                            <InfoCircle aria-label="Already up to date" className="text-fg-quaternary size-4" />
                        </TooltipTrigger>
                    </Tooltip>
                )}

                {phase === "error" && (
                    <Tooltip title={TAX_CALC_ERROR} placement="top">
                        <TooltipTrigger className="cursor-help">
                            <XCircle aria-label="Calculation failed" className="text-fg-error-primary size-4" />
                        </TooltipTrigger>
                    </Tooltip>
                )}
            </span>

            {/* `aria-disabled` + a guarded click rather than `isDisabled`.
              * A truly disabled button emits no pointer events, so react-aria
              * would never open its tooltip — and the before-Indications-Sent
              * tooltip is the one the user most needs to read. This keeps the
              * button hoverable while still announcing itself as unavailable
              * and refusing to fire. `isDisabled` is kept for the in-flight
              * case, where there is nothing to explain. */}
            <Tooltip title={canCalculate ? TOOLTIP_ENABLED : TOOLTIP_DISABLED} placement="top">
                <Button
                    color="secondary"
                    size="sm"
                    iconLeading={RefreshCw02}
                    isLoading={isLoading}
                    showTextWhileLoading
                    isDisabled={isLoading}
                    aria-disabled={!canCalculate || undefined}
                    data-autotest={dataAutotest ? `${dataAutotest}-calculate-button` : undefined}
                    // Figma sizes this pill at 108 × 32 (both hug). Atlas' `sm`
                    // gets the width right but runs 38 tall with a 20px glyph,
                    // so the height is pinned and the icon dropped to 16.
                    className={cx("h-8 py-0 *:data-icon:!size-4", !canCalculate && "cursor-not-allowed opacity-50")}
                    onClick={() => {
                        if (!canCalculate || isLoading) return;
                        onCalculate();
                    }}
                >
                    {isLoading ? "Calculating..." : "Calculate"}
                </Button>
            </Tooltip>
        </div>
    );
}
